// src/components/settings/email-templates-tab.jsx
import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import { DEFAULT_TEMPLATES } from "../../utils/openHouseEmails";
import { DEFAULT_ACCEPTANCE_TEMPLATES } from "../../utils/acceptanceEmails";
import { saveEmailTemplate, deleteEmailTemplate, loadEmailTemplates } from "../../utils/emailTemplateLoader";
import EmailTemplatesPicker from "./email-templates-picker";
import EmailTemplateForm from "./email-template-form";
import EmailTemplatePreview from "./email-template-preview";

function effectiveValues(templateId, customizations) {
  const d = DEFAULT_TEMPLATES[templateId] || DEFAULT_ACCEPTANCE_TEMPLATES[templateId] || {};
  const c = customizations || {};
  return {
    subject: c.subject ?? d.subject ?? "",
    body:    c.body    ?? d.body    ?? "",
    intro:   c.intro   ?? d.intro   ?? "",
    closing: c.closing ?? d.closing ?? "",
  };
}

export default function EmailTemplatesTab() {
  const [{ user, emailTemplates }, dispatch] = useStateValue();
  const [selectedId, setSelectedId] = useState("scheduling-seller");
  const isRecap = selectedId === "recap-seller";
  const saved = useMemo(
    () => effectiveValues(selectedId, emailTemplates?.[selectedId]),
    [selectedId, emailTemplates]
  );

  const [subject, setSubject] = useState(saved.subject);
  const [body, setBody] = useState(saved.body);
  const [intro, setIntro] = useState(saved.intro);
  const [closing, setClosing] = useState(saved.closing);
  const [saving, setSaving] = useState(false);

  // Sync drafts when the selected template (or its saved values) changes
  useEffect(() => {
    setSubject(saved.subject);
    setBody(saved.body);
    setIntro(saved.intro);
    setClosing(saved.closing);
  }, [selectedId, emailTemplates]);  // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = subject !== saved.subject
    || (!isRecap && body !== saved.body)
    || (isRecap && (intro !== saved.intro || closing !== saved.closing));

  const overridesForPreview = useMemo(() => {
    if (isRecap) return { subject, intro, closing };
    return { subject, body };
  }, [isRecap, subject, body, intro, closing]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const fields = isRecap ? { subject, intro, closing } : { subject, body };
      await saveEmailTemplate(selectedId, fields, user.id);
      const fresh = await loadEmailTemplates();
      dispatch({ type: actionType.SET_EMAIL_TEMPLATES, payload: fresh });
      toast.success("Template saved.");
    } catch (err) {
      console.error(err);
      toast.error(`Save failed: ${err.message || "unknown"}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setSubject(saved.subject);
    setBody(saved.body);
    setIntro(saved.intro);
    setClosing(saved.closing);
  };

  const handleReset = async () => {
    if (!window.confirm("Reset this template to its default text? Your customizations will be deleted.")) return;
    setSaving(true);
    try {
      await deleteEmailTemplate(selectedId);
      const fresh = await loadEmailTemplates();
      dispatch({ type: actionType.SET_EMAIL_TEMPLATES, payload: fresh });
      toast.success("Reset to default.");
    } catch (err) {
      console.error(err);
      toast.error(`Reset failed: ${err.message || "unknown"}`);
    } finally {
      setSaving(false);
    }
  };

  const modifiedIds = Object.keys(emailTemplates || {});

  return (
    <div className="bg-slate-50 rounded-lg overflow-hidden border border-slate-200">
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-3">
        <div className="text-sm font-semibold text-slate-700">Email Templates</div>
        <span className="bg-indigo-100 text-indigo-700 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded">Admins · Supervisors</span>
        <span className="flex-1"></span>
        <span className="text-xs text-slate-400">Team-wide. Changes affect emails sent by everyone.</span>
      </div>

      <div className="grid grid-cols-[220px_1fr_1fr] min-h-[600px]">
        <EmailTemplatesPicker
          selectedId={selectedId}
          onSelect={setSelectedId}
          modifiedIds={modifiedIds}
        />
        <EmailTemplateForm
          templateId={selectedId}
          isRecap={isRecap}
          subject={subject} onSubjectChange={setSubject}
          body={body} onBodyChange={setBody}
          intro={intro} onIntroChange={setIntro}
          closing={closing} onClosingChange={setClosing}
        />
        <EmailTemplatePreview
          templateId={selectedId}
          sender={user}
          overrides={overridesForPreview}
        />
      </div>

      <div className="flex items-center gap-2 px-5 py-3 bg-slate-50 border-t border-slate-200">
        <button
          type="button"
          onClick={handleReset}
          disabled={saving}
          className="text-xs font-semibold text-red-700 border border-red-200 rounded px-3 py-1.5 hover:bg-red-50 disabled:opacity-50"
        >
          Reset to default
        </button>
        <span className="flex-1"></span>
        <button
          type="button"
          onClick={handleDiscard}
          disabled={saving || !isDirty}
          className="text-xs font-semibold text-slate-600 border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-100 disabled:opacity-50"
        >
          Discard changes
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !isDirty}
          className="text-xs font-semibold text-white bg-slate-800 rounded px-3 py-1.5 hover:bg-slate-900 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
